import React from "react";
import {
  Table,
  TableRow,
  TableBody,
  useTheme,
  Box,
  Typography,
} from "@mui/material";
import { Tablecell } from "../../component/Methods";

const PortfolioSummary = ({ portfolioPeriodMetrics }) => {
  const theme = useTheme();

  if (!portfolioPeriodMetrics) {
    return null;
  }

  function roundit(number) {
    return ((number || 0) * 100).toFixed(2);
  }

  const transformedArray = [
    {
      name: "Week to Date",
      long: roundit(portfolioPeriodMetrics.WTD?.totalLong || 0),
      short: roundit(portfolioPeriodMetrics.WTD?.totalShort || 0),
      net: roundit(portfolioPeriodMetrics.WTD?.net || 0),
    },
    {
      name: "Month to Date",
      long: roundit(portfolioPeriodMetrics.MTD?.totalLong || 0),
      short: roundit(portfolioPeriodMetrics.MTD?.totalShort || 0),
      net: roundit(portfolioPeriodMetrics.MTD?.net || 0),
    },
    {
      name: "Quarter to Date",
      long: roundit(portfolioPeriodMetrics.QTD?.totalLong || 0),
      short: roundit(portfolioPeriodMetrics.QTD?.totalShort || 0),
      net: roundit(portfolioPeriodMetrics.QTD?.net || 0),
    },
    {
      name: "Last Quarter",
      long: roundit(portfolioPeriodMetrics.LQ?.totalLong || 0),
      short: roundit(portfolioPeriodMetrics.LQ?.totalShort || 0),
      net: roundit(portfolioPeriodMetrics.LQ?.net || 0),
    },
    {
      name: "Year to Date",
      long: roundit(portfolioPeriodMetrics.YTD?.totalLong || 0),
      short: roundit(portfolioPeriodMetrics.YTD?.totalShort || 0),
      net: roundit(portfolioPeriodMetrics.YTD?.net || 0),
    },
    {
      name: "Inception to Date",
      long: roundit(portfolioPeriodMetrics.ITD?.totalLong || 0),
      short: roundit(portfolioPeriodMetrics.ITD?.totalShort || 0),
      net: roundit(portfolioPeriodMetrics.ITD?.net || 0),
    },
  ];

  return (
    <Box sx={{ marginBottom: "16px" }}>
      {/* Title outside the box */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          fontSize: "16px",
          color: theme.palette.text.primary,
          marginBottom: "8px",
        }}
      >
        Portfolio Summary
      </Typography>

      {/* Summary Box */}
      <Box
        sx={{
          borderRadius: "8px",
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          overflow: "hidden",
          padding: "12px",
          maxWidth: "500px",
        }}
      >
        <Table
          size="small"
          aria-label="portfolio summary table"
          sx={{
            borderCollapse: "collapse",
            width: "100%",
          }}
        >
          <TableBody>
            {/* Header Row */}
            <TableRow
              sx={{
                //   backgroundColor: theme.palette.grey[100] || theme.palette.background.default,
              }}
            >
              <Tablecell
                align="left"
                sx={{
                  fontWeight: "900",
                  fontSize: "12px",
                  paddingLeft: "5px",
                  color: theme.palette.text.primary,
                  borderRight: "none",
                  borderLeft: "none",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                {/* Empty cell for period names */}
              </Tablecell>
              <Tablecell
                align="right"
                sx={{
                  fontWeight: "500",
                  fontSize: "12px",
                  paddingLeft: "5px",
                  color: theme.palette.text.secondary,
                  borderRight: "none",
                  borderLeft: "none",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                LONG
              </Tablecell>
              <Tablecell
                align="right"
                sx={{
                  fontWeight: "500",
                  fontSize: "12px",
                  paddingLeft: "5px",
                  color: theme.palette.text.secondary,
                  borderRight: "none",
                  borderLeft: "none",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                SHORT
              </Tablecell>
              <Tablecell
                align="right"
                sx={{
                  fontWeight: "500",
                  fontSize: "12px",
                  paddingLeft: "5px",
                  color: theme.palette.text.secondary,
                  borderRight: "none",
                  borderLeft: "none",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                NET
              </Tablecell>
            </TableRow>

            {/* Data Rows */}
            {transformedArray?.map((row, index) => (
              <TableRow
                key={index}
                sx={{
                  "&:hover": {
                    background: theme.palette.grey[200],
                    cursor: "pointer",
                  },
                  backgroundColor: "transparent",
                }}
              >
                <Tablecell
                  align="left"
                  sx={{
                    fontWeight: "900",
                    fontSize: "12px",
                    paddingLeft: "5px",
                    color: theme.palette.text.primary,
                    borderRight: "none",
                    borderLeft: "none",
                    borderTop: "none",
                    borderBottom: "none",
                  }}
                >
                  {row?.name}
                </Tablecell>

                <Tablecell
                  align="right"
                  sx={{
                    fontWeight: "500",
                    fontSize: "12px",
                    paddingLeft: "5px",
                    color: row?.long >= 0
                      ? theme.palette.success.main || "#4caf50"
                      : theme.palette.error.main || "#f44336",
                    borderRight: "none",
                    borderLeft: "none",
                    borderTop: "none",
                    borderBottom: "none",
                  }}
                >
                  {Math.abs(row?.long)}%
                </Tablecell>

                <Tablecell
                  align="right"
                  sx={{
                    fontWeight: "500",
                    fontSize: "12px",
                    paddingLeft: "5px",
                    color: row?.short >= 0
                      ? theme.palette.success.main || "#4caf50"
                      : theme.palette.error.main || "#f44336",
                    borderRight: "none",
                    borderLeft: "none",
                    borderTop: "none",
                    borderBottom: "none",
                  }}
                >
                  {Math.abs(row?.short)}%
                </Tablecell>

                <Tablecell
                  align="right"
                  sx={{
                    fontWeight: "500",
                    fontSize: "12px",
                    paddingLeft: "5px",
                    color: row?.net >= 0
                      ? theme.palette.success.main || "#4caf50"
                      : theme.palette.error.main || "#f44336",
                    borderRight: "none",
                    borderLeft: "none",
                    borderTop: "none",
                    borderBottom: "none",
                  }}
                >
                  {Math.abs(row?.net)}%
                </Tablecell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default PortfolioSummary;
